import { useEffect, useCallback } from 'react';
import { registerForEventAndSubscribe, unRegisterForEventAndUnsubscribe } from 'utils/ClientEventUtils';

/**
 * A hook to DRY up common ClientEventService register+subscribe, unregister+unsubscribe
 *
 * This can be safely used inside a component's initial rendering phase, before ID prop(s) are populated,
 * because without the ID the internal effect will be a NOOP.
 *
 * @param eventId to subscribe to
 * @param subScriberId subscriber id - required to ensure a particular subscription.
 * `useUniqueSubscriberId` hook can help.
 * @param listener
 */
export function useClientEventServiceSubscription<T = any>(
  eventId: string | null | undefined,
  subScriberId: string | null | undefined,
  listener: (event: T) => void
) {
  const handleNotification = useCallback(
    (e: CustomEventInit) => {
      listener(e.detail);
    },
    [listener]
  );

  useEffect(() => {
    if (eventId && subScriberId) {
      registerForEventAndSubscribe(eventId, subScriberId, handleNotification);

      return () => {
        unRegisterForEventAndUnsubscribe(eventId, eventId);
      };
    }
  }, [handleNotification, listener, eventId, subScriberId]);
}
